import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../hooks/useAuth';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  useTheme,
  alpha,
  Skeleton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import {
  History,
  Chat,
  Psychology,
  Refresh,
  Search,
  Clear,
  ExpandMore,
  ExpandLess,
  Delete,
  Visibility,
  MoreVert,
  CalendarToday,
  AccessTime,
  Person,
  SmartToy
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isToday, isYesterday, parseISO } from 'date-fns';

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3 }
  }
};

const ChatHistory = () => {
  const { session } = useAuth();
  const theme = useTheme();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuSession, setMenuSession] = useState(null);

  const fetchHistoryData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('ChatHistory: Fetching history data for user:', session?.user?.id);
      
      // Fetch conversation history
      const { data: conversationData, error: conversationError } = await supabase
        .from('conversations')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      console.log('ChatHistory: Raw conversation data:', conversationData);
      console.log('ChatHistory: Conversation error:', conversationError);

      if (conversationError) {
        console.error('Error fetching conversations:', conversationError);
      } else {
        const groupedConversations = groupConversationsByDay(conversationData || []);
        console.log('ChatHistory: Grouped conversations:', groupedConversations);
        setConversations(groupedConversations);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (session?.user?.id) {
      fetchHistoryData();
    }
  }, [session?.user?.id, fetchHistoryData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchHistoryData();
    setRefreshing(false);
  };

  const groupConversationsByDay = (conversations) => {
    const dayGroups = {};
    
    conversations.forEach((conv) => {
      const convDate = new Date(conv.created_at);
      const dayKey = convDate.toDateString();
      
      if (!dayGroups[dayKey]) {
        dayGroups[dayKey] = {
          id: `day-${dayKey}`,
          date: dayKey,
          startTime: conv.created_at,
          endTime: conv.created_at,
          messages: [],
          userMessages: 0,
          therapistMessages: 0,
          totalTokens: 0
        };
      }
      
      const dayGroup = dayGroups[dayKey];
      dayGroup.messages.push(conv);
      dayGroup.totalTokens += conv.tokens_used || 0;
      
      if (new Date(conv.created_at) < new Date(dayGroup.startTime)) {
        dayGroup.startTime = conv.created_at;
      }
      if (new Date(conv.created_at) > new Date(dayGroup.endTime)) {
        dayGroup.endTime = conv.created_at;
      }
      
      if (conv.sender === 'user') {
        dayGroup.userMessages++;
      } else {
        dayGroup.therapistMessages++;
      }
    });
    
    return Object.values(dayGroups).sort((a, b) => 
      new Date(b.startTime) - new Date(a.startTime)
    );
  };

  const formatDayHeader = (daySession) => {
    const sessionDate = new Date(daySession.startTime);
    if (isToday(sessionDate)) {
      return 'Today';
    } else if (isYesterday(sessionDate)) {
      return 'Yesterday';
    } else {
      return format(sessionDate, 'MMM d, yyyy');
    }
  };

  const getSessionDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const durationMinutes = Math.round((end - start) / (1000 * 60));
    return durationMinutes > 0 ? `${durationMinutes}m` : '<1m';
  };

  const getSessionPreview = (messages) => {
    if (!messages || messages.length === 0) return 'No messages';
    
    const firstUserMessage = messages.find(msg => msg.sender === 'user');
    if (firstUserMessage) {
      return firstUserMessage.message_text.length > 60 
        ? firstUserMessage.message_text.substring(0, 60) + '...'
        : firstUserMessage.message_text;
    }
    return 'Conversation started';
  };

  const getStatistics = () => {
    return {
      totalChatSessions: conversations.length,
      totalChatMessages: conversations.reduce((acc, session) => acc + session.messages.length, 0),
      todayChats: conversations.filter(session => isToday(new Date(session.startTime))).length
    };
  };

  const filteredConversations = conversations.filter(session => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return session.messages.some(msg => 
      msg.message_text.toLowerCase().includes(searchLower)
    ) || formatDayHeader(session).toLowerCase().includes(searchLower);
  });

  const handleSessionClick = (daySession) => {
    setSelectedSession(daySession);
    setViewDialogOpen(true);
  };

  const handleMenuClick = (event, session) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuSession(session);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuSession(null);
  };

  const handleViewSession = () => {
    console.log('View session clicked, menuSession:', menuSession);
    console.log('menuSession.messages:', menuSession?.messages);
    console.log('menuSession.messages.length:', menuSession?.messages?.length);
    setSelectedSession(menuSession);
    setViewDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteSession = () => {
    setSessionToDelete(menuSession);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    
    try {
      // Delete all messages for this session
      const { error } = await supabase
        .from('conversations')
        .delete()
        .eq('user_id', session.user.id)
        .gte('created_at', new Date(sessionToDelete.startTime).toISOString())
        .lt('created_at', new Date(new Date(sessionToDelete.endTime).getTime() + 24 * 60 * 60 * 1000).toISOString());

      if (error) throw error;

      // Refresh the conversation list
      await fetchHistoryData();
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const toggleSessionExpansion = (sessionId) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  if (loading) {
    console.log('ChatHistory: Loading state');
    return (
      <Box sx={{ mt: 2 }}>
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  console.log('ChatHistory: Rendering with conversations:', conversations.length);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Box sx={{ mt: 0.5, mb: 0.5 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mb: 1.5,
          px: 1
        }}>
          <Typography variant="h6" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            color: theme.palette.text.primary,
            fontWeight: 700,
            fontSize: '1rem'
          }}>
            <History sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            Chat History
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Refresh History">
              <IconButton 
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
                sx={{ 
                  color: theme.palette.primary.main,
                  '&:hover': { 
                    backgroundColor: alpha(theme.palette.primary.main, 0.1) 
                  }
                }}
              >
                <Refresh sx={{ 
                  fontSize: 16,
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 1.5, px: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search conversations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: theme.palette.background.paper,
                  borderColor: alpha(theme.palette.primary.main, 0.3)
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.background.paper,
                  borderColor: theme.palette.primary.main
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setSearchTerm('')}
                    size="small"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* Statistics Cards */}
        {conversations.length > 0 && (
          <Box sx={{ px: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ 
              color: theme.palette.text.secondary, 
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1
            }}>
              Statistics
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                }
              }}>
                <Typography variant="h6" sx={{ 
                  color: theme.palette.primary.main, 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  mb: 0.5
                }}>
                  {getStatistics().totalChatSessions}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Sessions
                </Typography>
              </Paper>
              
              <Paper sx={{ 
                p: 1.5, 
                textAlign: 'center',
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.15)}`
                }
              }}>
                <Typography variant="h6" sx={{ 
                  color: theme.palette.secondary.main, 
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  mb: 0.5
                }}>
                  {getStatistics().totalChatMessages}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Messages
                </Typography>
              </Paper>
            </Box>
          </Box>
        )}

        {/* Chat Sessions List */}
        <Box sx={{ px: 1 }}>
          <Typography variant="subtitle2" sx={{ 
            color: theme.palette.text.secondary, 
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 1
          }}>
            Chat Sessions
          </Typography>
          
          {filteredConversations.length === 0 ? (
            <Paper sx={{ 
              p: 3, 
              textAlign: 'center',
              backgroundColor: alpha(theme.palette.grey[50], 0.5),
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}>
              <Psychology sx={{ fontSize: 48, color: theme.palette.grey[400], mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 1 }}>
                {searchTerm ? 'No conversations match your search.' : 'No conversations yet.'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {searchTerm ? 'Try adjusting your search terms.' : 'Start a conversation to see your history here.'}
              </Typography>
              {searchTerm && (
                <Button 
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 1.5 }}
                  size="small"
                  variant="outlined"
                >
                  Clear Search
                </Button>
              )}
            </Paper>
          ) : (
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              <AnimatePresence>
                {filteredConversations.map((daySession, index) => (
                  <motion.div
                    key={daySession.id}
                    variants={itemVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    transition={{ delay: index * 0.05 }}
                  >
                    <Paper sx={{ 
                      mb: 1,
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.paper,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': { 
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        borderColor: alpha(theme.palette.primary.main, 0.2)
                      }
                    }}>
                      {/* Session Header */}
                      <Box sx={{ 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                      onClick={() => toggleSessionExpansion(daySession.id)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ 
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            width: 36,
                            height: 36,
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}>
                            <CalendarToday sx={{ fontSize: 18 }} />
                          </Avatar>
                          
                          <Box>
                            <Typography variant="subtitle1" sx={{ 
                              fontWeight: 700,
                              fontSize: '0.9rem',
                              color: theme.palette.text.primary
                            }}>
                              {formatDayHeader(daySession)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                              <Chip 
                                label={`${daySession.messages.length} messages`}
                                size="small"
                                clickable={false}
                                onClick={null}
                                sx={{ 
                                  height: 18,
                                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                                  color: theme.palette.info.main,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  pointerEvents: 'none'
                                }}
                              />
                              <Chip 
                                label={getSessionDuration(daySession.startTime, daySession.endTime)}
                                size="small"
                                clickable={false}
                                onClick={null}
                                sx={{ 
                                  height: 18,
                                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  color: theme.palette.success.main,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  pointerEvents: 'none'
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, daySession)}
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {expandedSessions.has(daySession.id) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>
                      </Box>

                      {/* Expanded Session Content */}
                      <Collapse in={expandedSessions.has(daySession.id)}>
                        <Box sx={{ px: 2, pb: 2 }}>
                          <Divider sx={{ mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ 
                            mb: 1,
                            fontSize: '0.8rem',
                            fontStyle: 'italic'
                          }}>
                            Session Preview: {getSessionPreview(daySession.messages)}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => {
                                console.log('Direct view button clicked, daySession:', daySession);
                                setSelectedSession(daySession);
                                setViewDialogOpen(true);
                              }}
                              sx={{ 
                                fontSize: '0.75rem',
                                textTransform: 'none'
                              }}
                            >
                              View Full Conversation
                            </Button>
                          </Box>
                          
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                            <Chip 
                              label={`${daySession.userMessages} your messages`}
                              size="small"
                              clickable={false}
                              onClick={null}
                              sx={{ 
                                height: 18,
                                backgroundColor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                pointerEvents: 'none'
                              }}
                            />
                            <Chip 
                              label={`${daySession.therapistMessages} therapist replies`}
                              size="small"
                              clickable={false}
                              onClick={null}
                              sx={{ 
                                height: 18,
                                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                color: theme.palette.warning.main,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                pointerEvents: 'none'
                              }}
                            />
                          </Box>
                        </Box>
                      </Collapse>
                    </Paper>
                  </motion.div>
                ))}
              </AnimatePresence>
            </Box>
          )}
        </Box>
        
        {/* Context Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 160,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
            }
          }}
        >
          <MenuItem onClick={handleViewSession}>
            <Visibility sx={{ mr: 1, fontSize: 18 }} />
            View Session
          </MenuItem>
          <MenuItem onClick={handleDeleteSession} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1, fontSize: 18 }} />
            Delete Session
          </MenuItem>
        </Menu>

        {/* View Session Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '80vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            pb: 2
          }}>
            <CalendarToday sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {selectedSession && formatDayHeader(selectedSession)}
            </Typography>
          </DialogTitle>
          
          <DialogContent sx={{ p: 0 }}>
            {(() => {
              console.log('Modal render - selectedSession:', selectedSession);
              console.log('Modal render - selectedSession?.messages:', selectedSession?.messages);
              return selectedSession ? (
                selectedSession.messages && selectedSession.messages.length > 0 ? (
                  <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    {[...selectedSession.messages]
                      .sort((a, b) => {
                        // Sort messages chronologically (oldest first)
                        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                        return timeA - timeB;
                      })
                      .map((message, index) => (
                      <ListItem key={index} sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        gap: 1,
                        py: 2,
                        borderBottom: index < selectedSession.messages.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : 'none'
                      }}>
                        <ListItemAvatar>
                          <Avatar sx={{ 
                            backgroundColor: message.sender === 'user' 
                              ? alpha(theme.palette.info.main, 0.1)
                              : alpha(theme.palette.success.main, 0.1),
                            color: message.sender === 'user' 
                              ? theme.palette.info.main
                              : theme.palette.success.main,
                            width: 32,
                            height: 32
                          }}>
                            {message.sender === 'user' ? <Person fontSize="small" /> : <SmartToy fontSize="small" />}
                          </Avatar>
                        </ListItemAvatar>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {message.sender === 'user' ? 'You' : 'AI Therapist'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {format(new Date(message.created_at), 'h:mm a')}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="body2" sx={{ 
                              color: theme.palette.text.primary,
                              lineHeight: 1.5,
                              mt: 0.5
                            }}>
                              {message.message_text}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No messages found in this session.
                    </Typography>
                  </Box>
                )
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Loading session data...
                  </Typography>
                </Box>
              );
            })()}
          </DialogContent>
          
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
            <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: { borderRadius: 3 }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete sx={{ color: 'error.main' }} />
            Delete Session
          </DialogTitle>
          
          <DialogContent>
            <Typography variant="body1">
              Are you sure you want to delete this chat session from{' '}
              <strong>{sessionToDelete && formatDayHeader(sessionToDelete)}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone. All messages from this session will be permanently deleted.
            </Typography>
          </DialogContent>
          
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={confirmDeleteSession} 
              variant="contained" 
              color="error"
              startIcon={<Delete />}
            >
              Delete Session
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default ChatHistory;
